import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { DatePicker } from "@/components/ui/DatePicker";
import { PageLoader } from "@/components/ui/Spinner";
import { saveBlobResponse } from "@/utils/download";
import { historicalLeavesApi, leavesApi } from "../api/leaves";
import {
  HISTORICAL_LEAVE_SOURCE_LABELS,
  LEAVE_TYPE_LABELS,
  fmtMinutesAsDays,
  type HistoricalLeave,
  type HistoricalLeaveSource,
  type LeaveBalanceReportRow,
  type LeaveCardYear,
  type LeaveType,
} from "../types";

const MONTH_LABELS = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII"];
const CONTRACT_LABELS: Record<string, string> = { uop: "UoP", uz: "UZ", b2b: "B2B" };

const fieldCls =
  "rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100";
const labelCls = "text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1";
const thCls = "text-left px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-gray-500 dark:text-gray-400 whitespace-nowrap";
const thNumCls = thCls.replace("text-left", "text-right");
const tdCls = "px-3 py-2.5 text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap";
const tdNumCls = `${tdCls} text-right tabular-nums`;
const sectionTitleCls = "font-mono text-[11px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400";

/** Minuty jako dni z maks. 2 miejscami (bez ogonków zer) — spójne z XLSX i polami formularza. */
function fmtDaysShort(minutes: number): string {
  return String(Math.round((minutes / 480) * 100) / 100);
}

function fmtDate(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}

function fmtRange(start: string, end: string): string {
  return start === end ? fmtDate(start) : `${fmtDate(start)} – ${fmtDate(end)}`;
}

/** Wartość w minutach jako tekst; zero jako wyciszony myślnik. */
function MinutesCell({ minutes, bold = false }: { minutes: number; bold?: boolean }) {
  if (minutes === 0) return <span className="text-gray-300 dark:text-gray-600">—</span>;
  return (
    <span className={`${bold ? "font-semibold text-gray-900 dark:text-gray-100" : ""} ${minutes < 0 ? "text-red-600 dark:text-red-400 font-semibold" : ""}`}>
      {fmtMinutesAsDays(minutes)}
    </span>
  );
}

function fmtHistoricalAmount(entry: HistoricalLeave): string {
  return fmtMinutesAsDays(
    entry.days_count * 480 + (entry.hours_count ?? 0) * 60 + (entry.minutes_count ?? 0),
  );
}

function invalidateBalances(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ["leave-card"] });
  queryClient.invalidateQueries({ queryKey: ["leave-balance-report"] });
  queryClient.invalidateQueries({ queryKey: ["leave-entitlement"] });
}

// ── Formularz bilansu otwarcia (wymiar + zbiorczy urlop wykorzystany sprzed ELGW5) ──

function OpeningBalanceForm({
  employeeId,
  year,
  onDone,
}: {
  employeeId: string;
  year: LeaveCardYear;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  // Empty pre-ELGW5 year: prefill the statutory default allowance instead of 0.
  const [daysTotal, setDaysTotal] = useState(String(year.has_data ? year.days_total : 26));
  // For a stub year the carryover is auto-derived from the previous year at
  // creation — sending the prefilled "0" would wipe it, so start empty and
  // only send a value the user actually typed.
  const [carryover, setCarryover] = useState(year.has_data ? String(year.prev_year_carryover) : "");
  const [histDays, setHistDays] = useState(String(Math.floor(year.historical_used_minutes / 480)));
  const [histHours, setHistHours] = useState(String(Math.floor((year.historical_used_minutes % 480) / 60)));
  const [note, setNote] = useState(year.historical_note);
  // Per-month overrides, edited in days ("" = no override, keep the system value).
  const [monthDays, setMonthDays] = useState<string[]>(() =>
    Array.from({ length: 12 }, (_, idx) => {
      const override = year.manual_monthly_minutes?.[idx];
      return override === null || override === undefined
        ? ""
        : String(Math.round((override / 480) * 100) / 100);
    }),
  );
  const [error, setError] = useState("");

  const save = useMutation({
    mutationFn: (manualMonthlyMinutes: (number | null)[]) =>
      leavesApi.patchEntitlement({
        employee: employeeId,
        year: year.year,
        days_total: parseInt(daysTotal, 10),
        // Omitted when left empty — the backend then derives it from the
        // previous year instead of storing a literal zero.
        ...(carryover.trim() !== "" ? { prev_year_carryover: parseInt(carryover, 10) } : {}),
        historical_used_minutes:
          parseInt(histDays || "0", 10) * 480
          + parseInt(histHours || "0", 10) * 60
          + (year.historical_used_minutes % 60),
        historical_note: note,
        manual_monthly_minutes: manualMonthlyMinutes,
      }),
    onSuccess: () => {
      invalidateBalances(queryClient);
      onDone();
    },
    onError: () => setError("Nie udało się zapisać bilansu. Spróbuj ponownie."),
  });

  const submit = () => {
    const days = parseInt(daysTotal, 10);
    if (daysTotal.trim() === "" || !Number.isFinite(days) || days < 0) {
      setError("Podaj wymiar urlopu (liczba dni).");
      return;
    }
    // A garbage month value must fail loudly — silently mapping it to null
    // would wipe an existing override on save.
    const monthly: (number | null)[] = [];
    for (let idx = 0; idx < 12; idx++) {
      const value = monthDays[idx].trim();
      if (value === "") {
        monthly.push(null);
        continue;
      }
      const dayCount = parseFloat(value.replace(",", "."));
      if (!Number.isFinite(dayCount) || dayCount < 0) {
        setError(`Nieprawidłowa wartość w miesiącu ${MONTH_LABELS[idx]} — podaj liczbę dni albo zostaw puste.`);
        return;
      }
      monthly.push(Math.round(dayCount * 480));
    }
    if (!year.has_data) {
      const anyUsage =
        parseInt(histDays || "0", 10) > 0
        || parseInt(histHours || "0", 10) > 0
        || monthly.some((m) => (m ?? 0) > 0);
      if (
        !anyUsage
        && !window.confirm(
          "Zapisujesz rok bez żadnego wykorzystania urlopu — powstanie pełna pula "
          + "niewykorzystanych dni, która zwiększy urlop zaległy w kolejnych latach. "
          + "Kontynuować?",
        )
      ) {
        return;
      }
    }
    setError("");
    save.mutate(monthly);
  };

  return (
    <div className="border border-[var(--color-border)] bg-gray-50 dark:bg-gray-800/50 p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className={sectionTitleCls}>Bilans otwarcia — {year.year}</span>
        <button aria-label="Zamknij formularz" title="Zamknij" className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={onDone}>
          <X size={14} />
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <label className={labelCls}>Wymiar (dni, pełny etat)</label>
          <input type="number" min={0} max={99} className={`${fieldCls} w-full`} value={daysTotal}
            onChange={(e) => setDaysTotal(e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Zaległy z poprz. roku (dni)</label>
          <input type="number" min={0} max={99} className={`${fieldCls} w-full`} value={carryover}
            placeholder={year.has_data ? undefined : "auto"}
            onChange={(e) => setCarryover(e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Wykorzystany przed ELGW5 — dni</label>
          <input type="number" min={0} max={366} className={`${fieldCls} w-full`} value={histDays}
            onChange={(e) => setHistDays(e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>— dodatkowo godzin (jeśli są)</label>
          <input type="number" min={0} max={7} className={`${fieldCls} w-full`} value={histHours}
            onChange={(e) => setHistHours(e.target.value)} />
        </div>
      </div>
      <div>
        <label className={labelCls}>Wykorzystanie w miesiącach (dni) — opcjonalnie</label>
        <div className="grid grid-cols-6 sm:grid-cols-12 gap-1">
          {MONTH_LABELS.map((label, idx) => {
            const systemMinutes = year.months_system_minutes[idx] ?? 0;
            return (
              <div key={label}>
                <span className="block text-center font-mono text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-0.5">
                  {label}
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  className={`${fieldCls} w-full px-1 py-1.5 text-center text-xs`}
                  value={monthDays[idx]}
                  placeholder={systemMinutes ? fmtDaysShort(systemMinutes) : "–"}
                  title={systemMinutes ? `Wg systemu: ${fmtMinutesAsDays(systemMinutes)}` : "Brak danych systemowych"}
                  onChange={(e) =>
                    setMonthDays((prev) => prev.map((v, i) => (i === idx ? e.target.value : v)))
                  }
                />
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
          Puste pole = miesiąc liczony automatycznie (szara podpowiedź to wartość z systemu).
          Wpisana liczba dni <strong>zastępuje</strong> wyliczenie systemowe dla tego miesiąca —
          przy rozjeździe z danymi ELGW5 zobaczysz ostrzeżenie.
        </p>
      </div>
      <div>
        <label className={labelCls}>Notatka (źródło danych, np. Excel kadr)</label>
        <input type="text" className={`${fieldCls} w-full`} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <p className="text-xs text-gray-400 dark:text-gray-500">
        Zbiorczy „wykorzystany przed ELGW5" i pojedyncze absencje historyczne sumują się — tej samej
        nieobecności nie wprowadzaj podwójnie. Zapis przelicza zaległy urlop w kolejnych latach.
      </p>
      {error && <p className="text-xs text-red-500">{error}</p>}
      <div className="flex gap-2">
        <Button size="sm" onClick={submit} disabled={save.isPending}>Zapisz</Button>
        <Button size="sm" variant="ghost" onClick={onDone}>Anuluj</Button>
      </div>
    </div>
  );
}

// ── Formularz pojedynczej historycznej absencji ──────────────────────────────

function HistoricalEntryForm({ employeeId, onDone }: { employeeId: string; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [leaveType, setLeaveType] = useState<LeaveType>("annual");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [days, setDays] = useState("1");
  const [hours, setHours] = useState("0");
  const [source, setSource] = useState<HistoricalLeaveSource>("karta_pracy");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");

  const create = useMutation({
    mutationFn: () =>
      historicalLeavesApi.create({
        employee: employeeId,
        leave_type: leaveType,
        start_date: startDate,
        end_date: endDate || startDate,
        days_count: parseInt(days || "0", 10),
        hours_count: parseInt(hours || "0", 10),
        minutes_count: 0,
        source,
        note,
      }),
    onSuccess: () => {
      invalidateBalances(queryClient);
      onDone();
    },
    onError: (err: unknown) => {
      const errors = (err as { response?: { data?: { errors?: Record<string, unknown> } } })
        ?.response?.data?.errors;
      const first = errors ? Object.values(errors)[0] : null;
      setError(Array.isArray(first) ? String(first[0]) : first ? String(first) : "Nie udało się dodać wpisu.");
    },
  });

  const submit = () => {
    if (!startDate) { setError("Podaj datę od."); return; }
    if (endDate && endDate < startDate) { setError("Data do nie może być przed datą od."); return; }
    setError("");
    create.mutate();
  };

  return (
    <div className="border border-[var(--color-border)] bg-gray-50 dark:bg-gray-800/50 p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className={sectionTitleCls}>Nowa historyczna absencja</span>
        <button aria-label="Zamknij formularz" title="Zamknij" className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={onDone}>
          <X size={14} />
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div>
          <label className={labelCls}>Rodzaj</label>
          <select className={`${fieldCls} w-full`} value={leaveType} onChange={(e) => setLeaveType(e.target.value as LeaveType)}>
            {Object.entries(LEAVE_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Data od</label>
          <DatePicker value={startDate} onChange={setStartDate} />
        </div>
        <div>
          <label className={labelCls}>Data do</label>
          <DatePicker value={endDate} onChange={setEndDate} placeholder="= data od" />
        </div>
        <div>
          <label className={labelCls}>Liczba dni urlopu (przepisz z karty)</label>
          <input type="number" min={0} max={366} className={`${fieldCls} w-full`} value={days}
            onChange={(e) => setDays(e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>— dodatkowo godzin (jeśli są)</label>
          <input type="number" min={0} max={23} className={`${fieldCls} w-full`} value={hours}
            onChange={(e) => setHours(e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Źródło</label>
          <select className={`${fieldCls} w-full`} value={source} onChange={(e) => setSource(e.target.value as HistoricalLeaveSource)}>
            {Object.entries(HISTORICAL_LEAVE_SOURCE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className={labelCls}>Uwagi</label>
        <input type="text" className={`${fieldCls} w-full`} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <p className="text-xs text-gray-400 dark:text-gray-500">
        Liczba dni wpisywana ręcznie (wg karty pracy / wniosku papierowego) — system nie wylicza jej
        z zakresu dat. Wpis nie może obejmować dwóch lat kalendarzowych.
      </p>
      {error && <p className="text-xs text-red-500">{error}</p>}
      <div className="flex gap-2">
        <Button size="sm" onClick={submit} disabled={create.isPending}>Dodaj</Button>
        <Button size="sm" variant="ghost" onClick={onDone}>Anuluj</Button>
      </div>
    </div>
  );
}

// ── Karta urlopowa pracownika (a'la Excel kadr) ──────────────────────────────

function LeaveCardModal({ employeeId, onClose }: { employeeId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { data: card, isLoading } = useQuery({
    queryKey: ["leave-card", employeeId],
    queryFn: () => leavesApi.getLeaveCard(employeeId),
  });
  const [editYear, setEditYear] = useState<number | null>(null);
  const [addingEntry, setAddingEntry] = useState(false);
  const [editingStart, setEditingStart] = useState(false);
  const [startDraft, setStartDraft] = useState("");

  const deleteEntry = useMutation({
    mutationFn: (id: string) => historicalLeavesApi.delete(id),
    onSuccess: () => invalidateBalances(queryClient),
    onError: () => window.alert("Nie udało się usunąć wpisu. Spróbuj ponownie."),
  });

  const saveEmploymentStart = useMutation({
    mutationFn: (value: string | null) => leavesApi.patchEmploymentStart(employeeId, value),
    onSuccess: () => {
      invalidateBalances(queryClient);
      setEditingStart(false);
    },
    onError: () => window.alert("Nie udało się zapisać daty zatrudnienia. Spróbuj ponownie."),
  });

  const submitEmploymentStart = () => {
    // Saving the unchanged derived date would needlessly pin it as a manual
    // override, detaching the card from future HR/payroll corrections.
    if (
      !card?.employee.employment_start_override
      && (startDraft || null) === (card?.employee.employment_start_date ?? null)
    ) {
      setEditingStart(false);
      return;
    }
    saveEmploymentStart.mutate(startDraft || null);
  };

  const approvedSystemLeaves = useMemo(
    () => (card?.system_leaves ?? []).filter((l) => l.status === "approved"),
    [card],
  );

  // Miesiące, w których ręczny wpis kadr różni się od wartości wyliczonej z ELGW5.
  const monthMismatches = useMemo(
    () =>
      (card?.years ?? []).flatMap((y) =>
        (y.manual_monthly_minutes ?? []).flatMap((override, idx) => {
          const system = y.months_system_minutes?.[idx] ?? 0;
          return override !== null && system > 0 && override !== system
            ? [{ year: y.year, monthIdx: idx, override, system }]
            : [];
        }),
      ),
    [card],
  );

  const monthTh = "px-1 py-2 font-mono text-[11px] uppercase tracking-wider text-gray-400 dark:text-gray-500 text-center w-8";
  const monthTd = "px-1 py-2.5 text-xs text-center tabular-nums";

  return (
    <Modal open onClose={onClose} title="Karta urlopowa" size="xl">
      {isLoading || !card ? (
        <PageLoader />
      ) : (
        <div className="flex flex-col gap-6 pb-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="text-lg font-bold text-gray-900 dark:text-gray-100">{card.employee.name}</span>
            {card.employee.contract_type && (
              <Badge color="blue">{CONTRACT_LABELS[card.employee.contract_type] ?? card.employee.contract_type}</Badge>
            )}
            {card.employee.branch_name && (
              <span className="text-xs text-gray-500 dark:text-gray-400">{card.employee.branch_name}</span>
            )}
            {card.employee.employment_fraction_numerator < card.employee.employment_fraction_denominator && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                etat {card.employee.employment_fraction_numerator}/{card.employee.employment_fraction_denominator}
              </span>
            )}
            {editingStart ? (
              <span className="inline-flex items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">zatrudniony od</span>
                <DatePicker value={startDraft} onChange={setStartDraft} />
                <Button
                  size="sm"
                  onClick={submitEmploymentStart}
                  disabled={saveEmploymentStart.isPending}
                >
                  Zapisz
                </Button>
                {card.employee.employment_start_override && (
                  <Button size="sm" variant="ghost" onClick={() => saveEmploymentStart.mutate(null)}>
                    Przywróć z kadr
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => setEditingStart(false)}>Anuluj</Button>
              </span>
            ) : (
              <button
                className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-[var(--color-primary)] group"
                title="Zmień datę zatrudnienia (pracownicy sprzed ELGW5)"
                onClick={() => {
                  setStartDraft(card.employee.employment_start_date ?? "");
                  setEditingStart(true);
                }}
              >
                {card.employee.employment_start_date
                  ? `zatrudniony od ${fmtDate(card.employee.employment_start_date)}`
                  : "uzupełnij datę zatrudnienia"}
                {card.employee.employment_start_override && (
                  <span className="text-[10px] text-amber-600 dark:text-amber-400">(wpisana ręcznie)</span>
                )}
                <Pencil size={11} className="opacity-40 group-hover:opacity-100" />
              </button>
            )}
          </div>

          <div className="border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10 px-4 py-3 text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
            <p className="font-semibold mb-1">Jak uzupełnić dane sprzed ELGW5?</p>
            <p>
              <strong>1.</strong> Kliknij <em>„Uzupełnij dane"</em> przy roku i przepisz z Excela kadr:
              wymiar urlopu, dni zaległe z poprzedniego roku oraz łączną liczbę dni już wykorzystanych
              (sprzed wdrożenia ELGW5).{" "}
              <strong>2.</strong> Jeśli masz karty pracy z datami, możesz zamiast tego dodać pojedyncze
              urlopy w sekcji <em>„Urlopy wpisane ręcznie"</em> — wtedy pojawią się też w rozbiciu na
              miesiące. <strong>Nie wpisuj tej samej nieobecności w obu miejscach</strong> — obie formy
              sumują się. System po każdym zapisie sam przelicza urlop zaległy na kolejne lata.
            </p>
          </div>

          <div className="border border-[var(--color-border)]">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className={thCls}>Rok</th>
                  <th className={thNumCls}>Zaległy</th>
                  <th className={thNumCls}>Wymiar</th>
                  <th className={thNumCls}>Suma</th>
                  {MONTH_LABELS.map((m) => (
                    <th key={m} className={monthTh}>{m}</th>
                  ))}
                  <th className={thNumCls}>Wykorzystany</th>
                  <th className={thNumCls}>Pozostało</th>
                  <th />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {card.years.map((y, rowIdx) => {
                  // Rows are newest-first — only the current (top) year is emphasised.
                  const isLatest = rowIdx === 0;
                  return (
                  <tr key={y.year} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className={`${tdCls} font-mono ${isLatest ? "font-semibold text-gray-900 dark:text-gray-100" : "text-gray-500 dark:text-gray-400"}`}>{y.year}</td>
                    <td className={tdNumCls}>
                      {!y.has_data || y.prev_year_carryover === 0
                        ? <span className="text-gray-300 dark:text-gray-600">—</span>
                        : y.prev_year_carryover}
                    </td>
                    <td className={tdNumCls}>
                      {y.has_data ? y.days_total : <span className="text-gray-300 dark:text-gray-600">—</span>}
                    </td>
                    <td className={`${tdNumCls} ${isLatest ? "font-medium text-gray-900 dark:text-gray-100" : ""}`}>
                      {y.has_data
                        ? y.days_total + y.prev_year_carryover
                        : <span className="font-normal text-gray-300 dark:text-gray-600">—</span>}
                    </td>
                    {y.months_minutes.map((minutes, idx) => {
                      const override = y.manual_monthly_minutes?.[idx] ?? null;
                      const system = y.months_system_minutes?.[idx] ?? 0;
                      const mismatch = override !== null && system > 0 && override !== system;
                      const cellStyle = mismatch
                        ? "text-amber-700 dark:text-amber-400 font-semibold bg-amber-100/80 dark:bg-amber-900/30"
                        : minutes
                          ? "text-gray-900 dark:text-gray-100 font-medium bg-blue-50/60 dark:bg-blue-900/20"
                          : "text-gray-200 dark:text-gray-700";
                      const cellTitle = mismatch
                        ? `Rozjazd danych: wpisano ręcznie ${fmtMinutesAsDays(override)}, wg ELGW5 ${fmtMinutesAsDays(system)}`
                        : override !== null
                          ? `Wpisane ręcznie: ${fmtMinutesAsDays(override)}`
                          : minutes
                            ? fmtMinutesAsDays(minutes)
                            : undefined;
                      return (
                        <td key={idx} className={`${monthTd} ${cellStyle}`} title={cellTitle}>
                          {minutes ? fmtDaysShort(minutes) : "·"}
                          {mismatch && "!"}
                        </td>
                      );
                    })}
                    <td className={tdNumCls}><MinutesCell minutes={y.used_minutes_total} /></td>
                    <td className={tdNumCls}>
                      <MinutesCell minutes={y.available_minutes_total} bold={isLatest} />
                      {isLatest && y.available_overdue_days > 0 && (
                        <span className="ml-1.5 text-[10px] text-amber-600 dark:text-amber-400">
                          w tym {y.available_overdue_days} zaległych
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <button
                        className={`inline-flex items-center gap-1 whitespace-nowrap border px-2 py-1 text-[11px] font-medium transition-colors ${
                          editYear === y.year
                            ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                            : "border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
                        }`}
                        onClick={() => setEditYear(editYear === y.year ? null : y.year)}
                      >
                        <Pencil size={11} /> Uzupełnij dane
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="px-3 py-1.5 text-[11px] text-gray-400 dark:text-gray-500 border-t border-gray-100 dark:border-gray-700/50">
              Kolumny I–XII: dni urlopu wypoczynkowego wykorzystane w danym miesiącu
              (wnioski z ELGW5 + urlopy wpisane ręcznie; wartości wpisane ręcznie zastępują wyliczenie).
            </p>
          </div>

          {monthMismatches.length > 0 && (
            <div className="border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-xs text-amber-800 dark:text-amber-300">
              <p className="font-semibold mb-1">Rozjazd danych ręcznych z ELGW5</p>
              <ul className="list-disc pl-4 space-y-0.5">
                {monthMismatches.map((m) => (
                  <li key={`${m.year}-${m.monthIdx}`}>
                    {MONTH_LABELS[m.monthIdx]} {m.year}: kadry wpisały{" "}
                    <strong>{fmtMinutesAsDays(m.override)}</strong>, a z wniosków w ELGW5 wynika{" "}
                    <strong>{fmtMinutesAsDays(m.system)}</strong>. Do puli liczona jest wartość
                    wpisana ręcznie — wyjaśnij różnicę albo wyczyść pole miesiąca.
                  </li>
                ))}
              </ul>
            </div>
          )}

          {editYear !== null && (() => {
            const yearRow = card.years.find((y) => y.year === editYear);
            return yearRow ? (
              <OpeningBalanceForm
                key={yearRow.year}
                employeeId={employeeId}
                year={yearRow}
                onDone={() => setEditYear(null)}
              />
            ) : null;
          })()}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <div>
              <div className="flex items-center justify-between mb-2 h-7">
                <span className={sectionTitleCls}>Urlopy wpisane ręcznie (sprzed ELGW5)</span>
                {!addingEntry && (
                  <button
                    className="inline-flex items-center gap-1 text-xs font-medium text-[var(--color-primary)] hover:underline"
                    onClick={() => setAddingEntry(true)}
                  >
                    <Plus size={13} /> Dodaj urlop z karty pracy
                  </button>
                )}
              </div>
              {addingEntry && (
                <div className="mb-3">
                  <HistoricalEntryForm employeeId={employeeId} onDone={() => setAddingEntry(false)} />
                </div>
              )}
              {card.historical_leaves.length === 0 ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 border border-dashed border-gray-200 dark:border-gray-700 px-3 py-4 text-center">
                  Brak wpisów — dane sprzed ELGW5 wpisują kadry z kart pracy i wniosków papierowych.
                </p>
              ) : (
                <div className="border border-[var(--color-border)] divide-y divide-gray-100 dark:divide-gray-700/50">
                  {card.historical_leaves.map((entry) => (
                    <div key={entry.id} className="px-3 py-2 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/30 group">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2">
                          <span className="text-xs font-medium text-gray-900 dark:text-gray-100 tabular-nums">
                            {fmtRange(entry.start_date, entry.end_date)}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{fmtHistoricalAmount(entry)}</span>
                        </div>
                        <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate">
                          {entry.leave_type_display} · {entry.source_display}
                          {entry.note ? ` · ${entry.note}` : ""}
                        </p>
                      </div>
                      <button
                        className="p-1 text-gray-300 dark:text-gray-600 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Usuń wpis"
                        onClick={() => {
                          if (window.confirm("Usunąć ten wpis historyczny?")) deleteEntry.mutate(entry.id);
                        }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center mb-2 h-7">
                <span className={sectionTitleCls}>Urlopy zatwierdzone w ELGW5</span>
              </div>
              {approvedSystemLeaves.length === 0 ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 border border-dashed border-gray-200 dark:border-gray-700 px-3 py-4 text-center">
                  Brak zatwierdzonych wniosków.
                </p>
              ) : (
                <div className="border border-[var(--color-border)] divide-y divide-gray-100 dark:divide-gray-700/50">
                  {approvedSystemLeaves.map((leave) => (
                    <div key={leave.id} className="px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs font-medium text-gray-900 dark:text-gray-100 tabular-nums">
                          {fmtRange(leave.start_date, leave.end_date)}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {leave.is_hourly
                            ? `${leave.hours_count ?? 0}h${leave.minutes_count ? ` ${leave.minutes_count}min` : ""}`
                            : `${leave.days_count} ${leave.days_count === 1 ? "dzień rob." : "dni rob."}`}
                        </span>
                      </div>
                      <p className="text-[11px] text-gray-400 dark:text-gray-500">{leave.leave_type_display}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── Strona: lista bilansów wszystkich pracowników ────────────────────────────

export function LeaveBalancesPage() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [cardEmployeeId, setCardEmployeeId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["leave-balance-report", year],
    queryFn: () => leavesApi.getBalanceReport(year),
  });

  const rows = useMemo(() => {
    const all: LeaveBalanceReportRow[] = data?.rows ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (r) => r.employee_name.toLowerCase().includes(q) || r.branch_name.toLowerCase().includes(q),
    );
  }, [data, search]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const resp = await leavesApi.exportBalanceReport(year);
      saveBlobResponse(resp, "bilans-urlopow.xlsx");
    } catch {
      window.alert("Nie udało się pobrać pliku. Spróbuj ponownie.");
    } finally {
      setExporting(false);
    }
  };

  // Ewidencja urlopów zaczyna się od 2024 — starszych lat nie ma i nie będzie.
  const FIRST_LEAVE_YEAR = 2024;
  const yearOptions = [];
  for (let y = currentYear; y >= FIRST_LEAVE_YEAR; y--) yearOptions.push(y);

  return (
    <div className="max-w-7xl">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Bilanse urlopowe</h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-1" />
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 max-w-2xl">
            Bilans otwarcia sprzed ELGW5 + wnioski w systemie. Otwórz kartę pracownika, aby wpisać
            dane historyczne i porównać wynik z Excelami kadr przed rozliczeniem zaległych urlopów.
          </p>
        </div>
        <Button variant="secondary" onClick={exportXlsx} disabled={exporting} title="Plik zawiera wszystkie lata wszystkich pracowników, niezależnie od wybranego roku">
          <Download size={14} className="mr-1.5" />
          {exporting ? "Eksportuję…" : "Eksport XLSX (pełna historia)"}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select
          className={`${fieldCls} w-28 font-mono`}
          value={year}
          onChange={(e) => setYear(parseInt(e.target.value, 10))}
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            className={`${fieldCls} w-72 pl-9`}
            placeholder="Szukaj pracownika lub oddziału…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {!isLoading && (
          <span className="ml-auto text-xs text-gray-400 dark:text-gray-500 tabular-nums">
            {rows.length} {rows.length === 1 ? "pracownik" : "pracowników"}
          </span>
        )}
      </div>

      {isLoading ? (
        <PageLoader />
      ) : (
        <div className="bg-white dark:bg-gray-800 border border-[var(--color-border)] overflow-x-auto">
          <table className="w-full min-w-[820px]">
            <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className={thCls}>Pracownik</th>
                <th className={thCls}>Oddział</th>
                <th className={thCls}>Umowa</th>
                <th className={thNumCls}>Wymiar urlopu</th>
                <th className={thNumCls}>Zaległy z poprz. roku</th>
                <th className={thNumCls}>Wykorzystany przed ELGW5</th>
                <th className={thNumCls}>Wykorzystany w ELGW5</th>
                <th className={thNumCls}>Pozostało</th>
                <th className={thNumCls}>W tym zaległy</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
              {rows.map((row) => (
                <tr
                  key={row.employee}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer focus:outline-none focus:bg-gray-50 dark:focus:bg-gray-700/50"
                  tabIndex={0}
                  aria-label={`Otwórz kartę urlopową: ${row.employee_name}`}
                  onClick={() => setCardEmployeeId(row.employee)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setCardEmployeeId(row.employee);
                    }
                  }}
                >
                  <td className={`${tdCls} font-medium text-[var(--color-primary)]`}>{row.employee_name}</td>
                  <td className={tdCls}>
                    {row.branch_name || <span className="text-gray-300 dark:text-gray-600">—</span>}
                  </td>
                  <td className={tdCls}>
                    <span className="text-gray-500 dark:text-gray-400">
                      {row.contract_type ? CONTRACT_LABELS[row.contract_type] ?? row.contract_type : "—"}
                    </span>
                  </td>
                  <td className={tdNumCls}>
                    {row.has_data ? row.days_total : <span className="text-gray-300 dark:text-gray-600">—</span>}
                  </td>
                  <td className={tdNumCls}>
                    {!row.has_data || row.prev_year_carryover === 0
                      ? <span className="text-gray-300 dark:text-gray-600">—</span>
                      : row.prev_year_carryover}
                  </td>
                  <td className={tdNumCls}><MinutesCell minutes={row.historical_used_total_minutes} /></td>
                  <td className={tdNumCls}><MinutesCell minutes={row.system_used_minutes} /></td>
                  <td className={tdNumCls}><MinutesCell minutes={row.available_minutes_total} bold /></td>
                  <td className={tdNumCls}>
                    {row.available_overdue_days > 0 ? (
                      <Badge color="yellow">{row.available_overdue_days} dni</Badge>
                    ) : (
                      <span className="text-gray-300 dark:text-gray-600">—</span>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={9} className={`${tdCls} text-center text-gray-400 dark:text-gray-500 py-8`}>
                    Brak pracowników do wyświetlenia.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {cardEmployeeId && (
        <LeaveCardModal employeeId={cardEmployeeId} onClose={() => setCardEmployeeId(null)} />
      )}
    </div>
  );
}
